import type {
  Grid,
  GridPosition,
  RandomSource,
  ReelColumn,
  ReelIndex,
  RowIndex,
  SymbolId,
} from '../core/types';
import { hasStickyWild, normalizeStickyWilds } from './sticky-wilds';
import type { PotOfGreedEffect } from './types';

export const POT_OF_GREED_CHANCE = 0.015;
export const POT_OF_GREED_AWARDED_SPINS = 1 as const;

export function getEligiblePotOfGreedPositions(
  grid: Grid,
  stickyWilds: readonly GridPosition[],
): readonly GridPosition[] {
  const normalizedStickyWilds = normalizeStickyWilds(stickyWilds);
  const eligible: GridPosition[] = [];
  grid.forEach((column, reel) => {
    column.forEach((symbol, row) => {
      if (hasStickyWild(normalizedStickyWilds, reel, row)) return;
      if (symbol === 'SCATTER' || symbol === 'WILD') return;
      eligible.push(Object.freeze({ reel: reel as ReelIndex, row: row as RowIndex }));
    });
  });
  return Object.freeze(eligible);
}

export function selectPotOfGreedPosition(
  grid: Grid,
  stickyWilds: readonly GridPosition[],
  alreadyTriggered: boolean,
  random: RandomSource,
  chance = POT_OF_GREED_CHANCE,
): GridPosition | null {
  if (!Number.isFinite(chance) || chance < 0 || chance > 1) {
    throw new RangeError('Die Topf-der-Gier-Chance muss zwischen 0 und 1 liegen.');
  }
  if (alreadyTriggered) return null;
  const eligible = getEligiblePotOfGreedPositions(grid, stickyWilds);
  if (eligible.length === 0 || random.next() >= chance) return null;
  const selected = eligible[random.integer(eligible.length)];
  if (selected === undefined) throw new Error('Topf-der-Gier-Position konnte nicht gewählt werden.');
  return selected;
}

export function createPotOfGreedEffect(position: GridPosition): PotOfGreedEffect {
  const [normalized] = normalizeStickyWilds([position]);
  if (normalized === undefined) throw new RangeError('Topf-der-Gier-Position fehlt.');
  return Object.freeze({
    position: normalized,
    awardedFreeSpins: POT_OF_GREED_AWARDED_SPINS,
    transformedToWild: true,
  });
}

/**
 * Diagnostic fixture only: the Legacy runtime inserted POG into the payline grid
 * before evaluation and could therefore destroy a regular win.
 */
export function applyLegacyPotOfGreedOverlay(
  grid: Grid,
  position: GridPosition,
): Grid<SymbolId> {
  const [normalized] = normalizeStickyWilds([position]);
  if (normalized === undefined) throw new RangeError('Topf-der-Gier-Position fehlt.');
  const columns = grid.map((column, reel) => Object.freeze(column.map((symbol, row) => (
    normalized.reel === reel && normalized.row === row ? 'POG' : symbol
  ))) as ReelColumn<SymbolId>);
  return Object.freeze(columns) as unknown as Grid<SymbolId>;
}

